'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { WriteOffCategory, WriteOffStatus } from '@repo/types';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Icon,
  Input,
  Select,
  Textarea,
  Tooltip,
} from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared';

import {
  ActionDialogMode,
  UpdateWriteOffPayload,
  WriteOff,
  WriteOffItem,
} from '../../types/writeOffs';
import { useQueryKeyStore } from '@xtnd/web/shared/stores';
import { AmountInput } from '../common';

/**
 * Supported field types for the action dialog
 */
type FieldType =
  | 'category'
  | 'comment'
  | 'amount'
  | 'confirmation'
  | 'note'
  | 'work-flow'
  | 'readonly'
  | 'section-header'
  | 'formula'
  | 'allocation-status'
  | 'allocation-label'
  | 'validation-error';

type Step = {
  index: number;
  label: string;
};

/**
 * Configuration for a single form field
 */
interface FieldConfig {
  type: FieldType;
  label: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  variant?: 'warning' | 'danger' | 'infos';
  steps?: Step[];
  /** For amount fields - key in formData to bind to */
  fieldKey?: keyof FormData;
  /** Optional condition to show/hide the field */
  condition?: (formData: FormData) => boolean;
  /** Optional flag to render the field in a half-width container */
  halfWidth?: boolean;
  value?: (formData: FormData) => any;
  maxValue?: number | ((formData: FormData) => number);
  minValue?: number | ((formData: FormData) => number);
  /** For formula fields - defines the parts: [label, value, label, value] joined by + = */
  formulaParts?: (formData: FormData) => { label: string; value: number }[];
}

/**
 * Configuration for a dialog mode
 */
interface ModeConfig {
  title: string;
  subTitle?: string;
  buttonText: string;
  buttonVariant: 'primary' | 'destructive';
  fields: FieldConfig[];
}

/**
 * Form data structure for all possible fields
 */
interface FormData {
  comment: string;
  category: WriteOffCategory;
  amount: number;
  feesCut?: number;
  expensesCut?: number;
  approvedFeesCut?: number;
  approvedExpensesCut?: number;
  isConfirmed: boolean;
}

interface ActionDialogProps {
  /** Controls the visibility state of the dialog */
  isOpen: boolean;
  /** Callback invoked when the dialog should close */
  onClose: () => void;
  /** The invoice item, or null if dialog is closed */
  writeOff: WriteOffItem | null;
  /** Dialog mode: WriteOffStatus.APPROVED, WriteOffStatus.REJECTED, WriteOffStatus.ARCHIVED, or 'note' */
  mode: ActionDialogMode;
  /** Category selection (only relevant for approve mode) */
  category?: WriteOffCategory;
  /** Handler for updating category selection */
  onCategoryChange?: (value: WriteOffCategory) => void;
  /** Handler for mutation state changes (loading indicators) */
  onMutationStateChange?: (writeOffId: string, isPending: boolean) => void;
  /** Submit handler - called with the built payload */
  onSubmit: (payload: UpdateWriteOffPayload) => Promise<WriteOffItem>;
}

// ===== MODE CONFIGURATION =====

/**
 * Declarative configuration for each dialog mode.
 * Defines: title, button text, button variant, and which fields to render.
 */
const MODE_ACTION_CONFIG = (
  writeOff: WriteOffItem | null
): Record<ActionDialogMode, ModeConfig> => {
  const invoiceNumber = writeOff?.invoice?.invoiceNumber ?? (writeOff as any)?.invoiceNumber ?? '-';
  const clientName = writeOff?.invoice?.clientName ?? (writeOff as any)?.clientName;
  return {
    [WriteOffStatus.APPROVED]: {
      title: `Approval - INV #${invoiceNumber}`,
      subTitle: `Approve write-off that was rejected by AR for ${clientName}`,
      buttonText: 'Confirm Approval',
      buttonVariant: 'primary',
      fields: [
        { type: 'category', label: 'Category', required: true },
        {
          type: 'comment',
          label: 'Approval Comments',
          required: true,
          placeholder: 'Enter approval notes...',
        },
        {
          type: 'note',
          label: 'Confirmation:',
          variant: 'infos',
          description: 'This item will be approved and moved to the Approved Writeoff Queue.',
        },
      ],
    },
    [WriteOffStatus.ESCALATED]: {
      title: `Request Review - INV #${invoiceNumber}`,
      buttonText: `Request Review`,
      buttonVariant: 'destructive',
      fields: [
        {
          type: 'allocation-label',
          label: 'Proposed Write-Off',
          value: () => writeOff?.amount || 0,
        } as FieldConfig,
        {
          type: 'section-header',
          label: 'Escalated Write-Off',
          description: 'These amounts will be sent for review in the escalation queue.',
        } as FieldConfig,
        {
          type: 'amount',
          fieldKey: 'feesCut',
          label: 'Escalated Fees Cut',
          required: true,
          halfWidth: true,
          minValue: 0,
          maxValue: writeOff?.amount || 0,
        },
        {
          type: 'amount',
          fieldKey: 'expensesCut',
          label: 'Escalated Expenses Cut',
          required: true,
          halfWidth: true,
          minValue: 0,
          maxValue: writeOff?.amount || 0,
        },
        {
          type: 'validation-error',
          label: 'Escalated fees and expenses must total more than $0.',
          condition: (formData) => (formData.feesCut || 0) + (formData.expensesCut || 0) <= 0,
        } as FieldConfig,
        {
          type: 'section-header',
          label: 'Approved Write-Off',
          description: 'These amounts will be approved.',
        } as FieldConfig,
        {
          type: 'amount',
          fieldKey: 'approvedFeesCut',
          label: 'Approved Fees Cut',
          required: true,
          halfWidth: true,
          minValue: 0,
          maxValue: writeOff?.amount || 0,
        },
        {
          type: 'amount',
          fieldKey: 'approvedExpensesCut',
          label: 'Approved Expenses Cut',
          required: true,
          halfWidth: true,
          minValue: 0,
          maxValue: writeOff?.amount || 0,
        },
        {
          type: 'category',
          label: 'Approved Category',
          required: true,
        },
        {
          type: 'allocation-status',
          label: 'Total Proposed Write-Off',
        } as FieldConfig,
        {
          type: 'confirmation',
          label: 'Partial Rejection Review',
          required: true,
        },
        {
          type: 'comment',
          label: 'Comment',
          required: true,
          placeholder: 'Add any comments or reasons for this write-off...',
        },
      ],
    },
    [WriteOffStatus.REJECTED]: {
      title: `Reject & Restart Appeal - INV #${invoiceNumber}`,
      subTitle: `Reject write-off and restart appeal process`,
      buttonText: `Reject & Restart Appeal`,
      buttonVariant: 'destructive',
      fields: [
        {
          type: 'work-flow',
          label: 'Appeal Restart Flow',
          steps: [
            { index: 1, label: 'Restart Appeal' },
            { index: 2, label: 'Notify Lawyers' },
            { index: 3, label: 'New Appeal' },
          ],
        },
        {
          type: 'comment',
          label: 'Reason for restarting appeal',
          required: true,
          placeholder: 'Enter reason for rejection and appeal restart...',
        },
        {
          type: 'note',
          label: 'Important Note:',
          variant: 'danger',
          description:
            'The system will restart the "To Be Appealed" status and notify the legal team to submit a new appeal.',
        },
      ],
    },
    [WriteOffStatus.ARCHIVED]: {
      title: `Archive - INV #${invoiceNumber}`,
      buttonText: 'Mark as Processed',
      buttonVariant: 'primary',
      fields: [
        {
          type: 'comment',
          label: 'Comment',
          placeholder: 'Add any comments about processing this write-off...',
        },
      ],
    },
    note: {
      title: `Comment - INV #${invoiceNumber}`,
      buttonText: 'Comment',
      buttonVariant: 'primary',
      fields: [
        {
          type: 'comment',
          label: 'Comment',
          placeholder: 'Add a comment to this invoice...',
        },
      ],
    },
  };
};

// ===== INITIAL FORM STATE =====

const INITIAL_FORM_DATA = (writeOff: WriteOffItem | null): FormData => {
  const amount = parseFloat((writeOff?.amount || 0).toFixed(2));
  const feesCut = parseFloat((writeOff?.feesCut || 0).toFixed(2));
  const expensesCut = parseFloat((writeOff?.expensesCut || 0).toFixed(2));
  return {
    comment: '',
    category: writeOff?.category || WriteOffCategory.WRITE_OFF,
    amount,
    feesCut,
    expensesCut,
    approvedFeesCut: 0,
    approvedExpensesCut: 0,
    isConfirmed: false,
  };
};

// ===== SUB-COMPONENTS =====

/**
 * Displays invoice details in a two-column grid layout
 */
function InvoiceDetails({
  invoice,
  amount,
  mode,
}: {
  invoice: WriteOff | undefined;
  amount: number;
  mode: ActionDialogMode;
}) {
  const mapModeToAmountLabels: Record<string, [label: string, color: string]> = {
    [WriteOffStatus.REJECTED]: ['Amount to Be Appealed', 'text-red-400'],
    [WriteOffStatus.APPROVED]: ['Amount to Approve', 'text-emerald-400'],
  };
  const [amountLabel, amountColor] = mapModeToAmountLabels[mode] ?? ['Amount', ''];

  return (
    <div className="rounded-lg py-2 text-sm text-xtnd-dark dark:text-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs dark:text-slate-400 font-semibold">Invoice</div>
          <div className="font-medium">#{invoice?.invoiceNumber}</div>
        </div>

        <div className="text-right">
          <div className="text-xs dark:text-slate-400 font-semibold">{amountLabel}</div>
          <div className={`font-semibold ${amountColor ?? ''}`}>
            {numberUtil.formatCurrency(amount)}
          </div>
        </div>
      </div>

      <div className="mt-2">
        <span className="text-xs dark:text-slate-400 font-semibold">Client</span>
        <div className="font-medium">{invoice?.clientName}</div>
      </div>

      <div className="mt-3 border-slate-700">
        <div className="text-xs tracking-wide dark:text-slate-400 font-semibold">Matter</div>
        <div className="leading-snug">{invoice?.matterDisplayNumber}</div>
      </div>
    </div>
  );
}

/**
 * Options for the category selector
 */
const CATEGORY_OPTIONS = [
  { label: 'Write Off', value: WriteOffCategory.WRITE_OFF },
  { label: 'Bad Debt', value: WriteOffCategory.BAD_DEBT },
];

/**
 * Category selector - renders a dropdown for Write Off / Bad Debt
 */
function CategorySelector({
  selectedCategory,
  onCategoryChange,
  required,
}: {
  selectedCategory: WriteOffCategory;
  onCategoryChange: (value: WriteOffCategory) => void;
  required?: boolean;
}) {
  return (
    <Select
      label="Approved Category"
      options={CATEGORY_OPTIONS}
      value={selectedCategory}
      onChange={opt => {
        const val = typeof opt === 'string' ? opt : opt?.value;
        if (val) onCategoryChange(val as WriteOffCategory);
      }}
      fullWidth
      className="text-sm"
      required={required}
    />
  );
}

/**
 * Comment textarea component
 */
function CommentInput({
  comment,
  onCommentChange,
  placeholder,
  label,
  required,
}: {
  comment: string;
  onCommentChange: (value: string) => void;
  placeholder: string;
  label: string;
  required?: boolean;
}) {
  return (
    <Textarea
      id="comment"
      label={label}
      value={comment}
      onChange={e => onCommentChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      fullWidth
      required={required}
    />
  );
}

const VARIANT_STYLES: Record<'warning' | 'danger' | 'infos', string> = {
  warning:
    'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-600 dark:bg-transparent dark:text-yellow-400',
  danger:
    'border-red-300 dark:border-red-300 bg-red-100 dark:bg-red-100 text-xtnd-dark dark:text-xtnd-dark',
  infos:
    'border-green-300 bg-green-50 text-green-800 dark:border-green-600 dark:bg-transparent dark:text-green-400',
};

// ===== MAIN COMPONENT =====

/**
 * ActionDialog Component
 *
 * A modal dialog for managing write-off invoice actions. Supports multiple modes:
 * - 'approved': Approve write-off with category selection (Write Off or Bad Debt)
 * - 'rejected': Reject write-off with optional comment
 * - 'archived': Mark write-off as processed/archived
 * - 'note': Add comment without changing status
 *
 * Features:
 * - Configuration-driven field rendering
 * - Automatic form reset when dialog closes
 * - Displays invoice information (Invoice #, Matter, Client, Amount)
 * - Loading state during submission
 * - Optimistic cache updates for React Query
 */
export default function ActionDialog({
  isOpen,
  onClose,
  writeOff,
  mode,
  onCategoryChange,
  onMutationStateChange,
  onSubmit,
}: ActionDialogProps) {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();

  const [formData, setFormData] = useState<FormData>(() => INITIAL_FORM_DATA(writeOff));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modeConfig = useMemo(() => MODE_ACTION_CONFIG(writeOff)[mode], [writeOff, mode]);

  /**
   * Validates all amount fields for the current mode
   */
  const areAllAmountFieldsValid = useMemo(() => {
    const amountFields = modeConfig.fields.filter(f => f.type === 'amount');
    if (amountFields.length === 0) return true;

    return amountFields.every(fieldConfig => {
      const fieldKey = fieldConfig.fieldKey || 'amount';
      const value = formData[fieldKey] as number;

      // Get minValue - if explicitly set to 0, allow 0; otherwise default to 0.01
      const explicitMinValue = fieldConfig.minValue;
      const minValue =
        typeof explicitMinValue === 'function'
          ? explicitMinValue(formData)
          : explicitMinValue !== undefined
            ? explicitMinValue
            : 0.01;

      const maxValue =
        typeof fieldConfig.maxValue === 'function'
          ? fieldConfig.maxValue(formData)
          : fieldConfig.maxValue ?? 0;

      // Validate value is within min and max bounds
      // If minValue is explicitly 0, allow 0 as valid; otherwise require >= minValue
      const isValidMin = explicitMinValue === 0 ? value >= 0 : value >= minValue;
      // maxValue is always defined in our configs, so check <= maxValue (0 is a valid max)
      const isValidMax = value <= maxValue;

      return isValidMin && isValidMax;
    });
  }, [modeConfig.fields, formData]);

  /**
   * Updates a single field in formData.
   * For escalated mode, derives amount from feesCut + expensesCut.
   */
  const updateField = useCallback(
    <K extends keyof FormData>(field: K, value: FormData[K]) => {
      setFormData(prev => {
        const next = { ...prev, [field]: value };

        if (mode === WriteOffStatus.ESCALATED) {
          // Derive escalated total (amount) from feesCut + expensesCut for the payload
          if (field === 'feesCut' || field === 'expensesCut') {
            next.amount = Number(((next.feesCut || 0) + (next.expensesCut || 0)).toFixed(2));
          }
        }

        return next;
      });
    },
    [mode]
  );

  /**
   * Resets all form fields to their initial values.
   */
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA(writeOff));
    onCategoryChange?.(WriteOffCategory.WRITE_OFF);
  }, [writeOff, onCategoryChange]);

  /**
   * Builds the update payload based on current form state and mode.
   */
  const buildPayload = useCallback((): UpdateWriteOffPayload | null => {
    if (!writeOff) return null;

    // For non-flattened rows (WriteOff), writeOffId is nested in writeOffs array
    const writeOffId =
      writeOff.writeOffId ?? (writeOff as unknown as WriteOff)?.writeOffs?.[0]?.writeOffId;

    const payload: UpdateWriteOffPayload = {
      writeOffId,
      invoiceId: writeOff.invoiceId,
      comment: formData.comment,
    };

    if (mode !== 'note') {
      payload.status = mode;

      if (mode === WriteOffStatus.APPROVED) {
        payload.category = formData.category;
      }

      if (mode === WriteOffStatus.ESCALATED) {
        payload.amount = formData.amount;
        payload.feesCut = formData.feesCut;
        payload.approvedFeesCut = formData.approvedFeesCut;
        payload.category = formData.category;
      }
    }

    return payload;
  }, [
    writeOff,
    mode,
    formData.comment,
    formData.category,
    formData.amount,
    formData.feesCut,
    formData.approvedFeesCut,
  ]);

  /**
   * Safely closes the dialog only when not in loading state.
   */
  const handleOnClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      resetForm();
    }
  }, [isSubmitting, onClose, resetForm]);

  /**
   * Handles form submission with validation and cleanup.
   */
  const handleSubmit = useCallback(async () => {
    if (!writeOff) return;

    const payload = buildPayload();
    if (!payload) return;

    const writeOffId = writeOff.writeOffId;
    onMutationStateChange?.(writeOffId, true);
    setIsSubmitting(true);

    try {
      await onSubmit(payload);
      onClose();
      resetForm();
    } finally {
      onMutationStateChange?.(writeOffId, false);
      setIsSubmitting(false);
      if (mode === WriteOffStatus.REJECTED) {
        const queryKey = getQueryKey('ar-collections', 'reporting.appeal');
        queryClient.invalidateQueries({ queryKey });
      }
    }
  }, [
    writeOff,
    buildPayload,
    onSubmit,
    onMutationStateChange,
    onClose,
    resetForm,
    mode,
    getQueryKey,
    queryClient,
  ]);

  // ===== EFFECTS =====

  /**
   * Initialize form when dialog opens with an invoice
   */
  useEffect(() => {
    if (isOpen && writeOff) {
      setFormData(INITIAL_FORM_DATA(writeOff));
    }
  }, [isOpen, writeOff]);

  /**
   * Renders a form field based on its configuration
   */
  const renderField = useCallback(
    (fieldConfig: FieldConfig, index: number) => {
      const key = `field-${fieldConfig.type}-${index}`;

      switch (fieldConfig.type) {
        case 'category':
          return (
            <CategorySelector
              key={key}
              selectedCategory={formData.category}
              onCategoryChange={(value: WriteOffCategory) => {
                updateField('category', value);
                onCategoryChange?.(value);
              }}
              required={fieldConfig.required}
            />
          );

        case 'comment':
          const commentKey = fieldConfig.fieldKey || 'comment';
          return (
            <CommentInput
              key={key}
              comment={formData[commentKey] as string}
              onCommentChange={value => updateField(commentKey, value)}
              placeholder={fieldConfig.placeholder || ''}
              label={fieldConfig.label}
              required={fieldConfig.required}
            />
          );
        case 'confirmation':
          const escalatedTotal = (formData.feesCut || 0) + (formData.expensesCut || 0);
          const approvedTotal = (formData.approvedFeesCut || 0) + (formData.approvedExpensesCut || 0);

          return (
            <div key={key} className="py-2 text-sm">
              <div className="bg-green-50 dark:bg-green-900/10 p-4 border border-green-200 dark:border-green-800/50 rounded-xl transition-colors cursor-pointer">
                <div className="flex items-center gap-2">
                  {' '}
                  <Checkbox
                    checked={formData.isConfirmed}
                    onChange={e => updateField('isConfirmed', e.target.checked)}
                    required={fieldConfig.required}
                  />{' '}
                  Confirm Request Review
                  <span className="text-red-500" aria-label="required">
                    *
                  </span>
                </div>

                <div className="text-sm mt-3 space-y-3">
                  <div>
                    <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">
                      Escalated Write-Off{' '}
                      <Tooltip content="A new escalated write-off record will be created and sent for review.">
                        <Icon name="FaInfoCircle" size="xs" className="cursor-help" />
                      </Tooltip>
                      :{' '}
                    </span>
                    <span className="font-semibold text-red-600">
                      {numberUtil.formatCurrency(escalatedTotal)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {' '}(Escalated Fees + Escalated Expenses)
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">
                      Approved Write-Off{' '}
                      <Tooltip content={`A new approved write-off record will be created with category ${CATEGORY_OPTIONS.find(o => o.value === formData.category)?.label || formData.category}.`}>
                        <Icon name="FaInfoCircle" size="xs" className="cursor-help" />
                      </Tooltip>
                      :{' '}
                    </span>
                    <span className="font-semibold text-green-700">
                      {numberUtil.formatCurrency(approvedTotal)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {' '}(Approved Fees + Approved Expenses)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );

        case 'amount':
          const amountKey = fieldConfig.fieldKey || 'amount';
          return (
            <AmountInput
              key={key}
              value={formData[amountKey] as number}
              onChange={value => updateField(amountKey, value)}
              label={fieldConfig.label}
              placeholder={fieldConfig.placeholder}
              required={fieldConfig.required}
              minAmount={
                typeof fieldConfig?.minValue === 'function'
                  ? fieldConfig?.minValue(formData)
                  : (fieldConfig?.minValue ?? 0.01)
              }
              maxAmount={
                typeof fieldConfig?.maxValue === 'function'
                  ? fieldConfig?.maxValue(formData)
                  : (fieldConfig?.maxValue ?? 0)
              }
            />
          );

        case 'readonly':
          return (
            <Input
              key={key}
              type="text"
              label={fieldConfig.label}
              value={fieldConfig.value?.(formData)}
              placeholder={fieldConfig.placeholder}
              fullWidth
              disabled={true}
            />
          );

        case 'formula': {
          const parts = fieldConfig.formulaParts?.(formData) || [];
          const total = fieldConfig.value?.(formData) ?? 0;
          return (
            <div
              key={key}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3"
            >
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">
                {fieldConfig.label}
              </div>
              <div className="flex items-center gap-2 text-sm">
                {parts.map((part, idx) => (
                  <span key={idx} className="flex items-center gap-2">
                    {idx > 0 && (
                      <span className="text-slate-400 font-medium">+</span>
                    )}
                    <span className="text-slate-600 dark:text-slate-300">
                      {part.label}:{' '}
                      <span className="font-semibold">
                        {numberUtil.formatCurrency(part.value)}
                      </span>
                    </span>
                  </span>
                ))}
                <span className="text-slate-400 font-medium">=</span>
                <span className="font-bold text-xtnd-dark dark:text-xtnd-white">
                  {numberUtil.formatCurrency(total)}
                </span>
              </div>
            </div>
          );
        }

        case 'allocation-status': {
          const totalProposed = writeOff?.amount || 0;
          const totalAllocated = Number(((formData.feesCut || 0) + (formData.expensesCut || 0) + (formData.approvedFeesCut || 0) + (formData.approvedExpensesCut || 0)).toFixed(2));
          const remaining = Number((totalProposed - totalAllocated).toFixed(2));
          const isMatched = remaining === 0;
          const isExceeded = remaining < 0;

          const statusStyles = isMatched
            ? { border: 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/10', icon: 'text-green-600', title: 'text-green-800 dark:text-green-400', desc: 'text-green-700 dark:text-green-500', label: 'text-green-600 dark:text-green-400', amount: 'text-green-800 dark:text-green-300' }
            : isExceeded
              ? { border: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/10', icon: 'text-red-600', title: 'text-red-800 dark:text-red-400', desc: 'text-red-700 dark:text-red-500', label: 'text-red-600 dark:text-red-400', amount: 'text-red-800 dark:text-red-300' }
              : { border: 'border-yellow-300 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/10', icon: 'text-yellow-600', title: 'text-yellow-800 dark:text-yellow-400', desc: 'text-yellow-700 dark:text-yellow-500', label: 'text-yellow-600 dark:text-yellow-400', amount: 'text-yellow-800 dark:text-yellow-300' };

          const statusTitle = isMatched
            ? 'Amounts match perfectly.'
            : isExceeded
              ? 'Amounts exceeded.'
              : 'Action Required: Match Invoice Amount';

          const statusDesc = isMatched
            ? 'You have successfully allocated the total invoice amount.'
            : isExceeded
              ? `Total allocated exceeds the proposed write-off by ${numberUtil.formatCurrency(Math.abs(remaining))}.`
              : `You must allocate the remaining ${numberUtil.formatCurrency(remaining)} to proceed.`;

          return (
            <div
              key={key}
              className={`rounded-xl border px-4 py-3 flex items-center justify-between ${statusStyles.border}`}
            >
              <div className="flex items-start gap-2">
                <Icon
                  name={isMatched ? 'FaCheckCircle' : 'FaExclamationCircle'}
                  size="sm"
                  className={`mt-0.5 shrink-0 ${statusStyles.icon}`}
                />
                <div>
                  <div className={`font-semibold text-sm ${statusStyles.title}`}>
                    {statusTitle}
                  </div>
                  <div className={`text-xs mt-0.5 ${statusStyles.desc}`}>
                    {statusDesc}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0 ml-4">
                <div className={`text-xs font-semibold uppercase ${statusStyles.label}`}>
                  Total Allocated
                </div>
                <div className={`text-lg font-bold ${statusStyles.amount}`}>
                  {numberUtil.formatCurrency(totalAllocated)}
                </div>
              </div>
            </div>
          );
        }

        case 'note':
          return (
            <div
              className={`text-xtnd-dark dark:text-xtnd-blue rounded-lg border border-blue-300 dark:border-xtnd-dark bg-blue-50 dark:bg-transparent px-4 py-3 text-sm ${fieldConfig.variant ? VARIANT_STYLES[fieldConfig.variant] : ''}`}
            >
              <span className="flex items-center gap-2 font-medium">
                <Icon name="FaInfoCircle" size="sm" />
                {fieldConfig.label}
              </span>
              <p className="mt-1 text-sm">{fieldConfig.description}</p>
            </div>
          );

        case 'allocation-label':
          return (
            <div key={key}>
              <div className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white">
                {fieldConfig.label}
              </div>
              <div className="text-lg font-bold text-xtnd-dark dark:text-xtnd-white">
                {numberUtil.formatCurrency(fieldConfig.value?.(formData) ?? 0)}
              </div>
            </div>
          );

        case 'section-header':
          return (
            <div key={key} className="border-b border-slate-200 dark:border-slate-700 pb-1">
              <h3 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white">
                {fieldConfig.label}
              </h3>
              {fieldConfig.description && (
                <p className="text-xs text-xtnd-dark dark:text-slate-400 mt-0.5">
                  {fieldConfig.description}
                </p>
              )}
            </div>
          );

        case 'work-flow':
          const getStepStyle = (step: number) => {
            switch (step) {
              case 1:
                return 'bg-red-100 text-red-600';
              case 2:
                return 'bg-yellow-100 text-yellow-600';
              case 3:
                return 'bg-blue-100 text-blue-600';
              default:
                return 'bg-gray-100 text-gray-600';
            }
          };

          return (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-6 py-4">
              <div className="mb-4 flex items-center gap-2 text-yellow-700 font-medium">
                <span>{fieldConfig.label}</span>
              </div>

              <div className="flex items-center gap-4">
                {fieldConfig.steps &&
                  fieldConfig.steps.map((step, idx) => (
                    <div key={step.index} className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span
                          className={`
                  flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold
                  ${getStepStyle(step.index)}
                `}
                        >
                          {step.index}
                        </span>
                        <span className="text-sm font-medium text-gray-800">{step.label}</span>
                      </div>

                      {idx < (fieldConfig.steps?.length || 0) - 1 && (
                        <span className="text-gray-400">→</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          );

        case 'validation-error':
          return (
            <p key={key} className="text-sm text-red-500">
              {fieldConfig.label}
            </p>
          );

        default:
          return null;
      }
    },
    [formData, updateField, onCategoryChange, writeOff]
  );
  // ===== UI CONFIG BY MODE =====
  return (
    <Dialog
      open={isOpen}
      onClose={handleOnClose}
      size="xl"
      ariaLabel={modeConfig.title}
      showCloseButton={false}
      className="flex flex-col"
    >
      <DialogHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
          {modeConfig.title}
        </h2>
        {modeConfig.subTitle && (
          <small className="text-xtnd-dark dark:text-xtnd-white">{modeConfig.subTitle}</small>
        )}
      </DialogHeader>
      <DialogBody className="overflow-y-auto">
        <div className='space-y-1'>
          {/* Invoice Information */}
          <InvoiceDetails invoice={writeOff?.invoice ?? (writeOff as unknown as WriteOff)} amount={writeOff?.amount ?? 0} mode={mode} />

          {/* Render fields based on mode configuration */}
          <div className="grid grid-cols-2 gap-4">
            {modeConfig.fields
              .filter(fieldConfig => !fieldConfig.condition || fieldConfig.condition(formData))
              .map((fieldConfig, index) => (
                <div
                  key={`field-container-${index}`}
                  className={fieldConfig.halfWidth ? 'col-span-1' : 'col-span-2'}
                >
                  {renderField(fieldConfig, index)}
                </div>
              ))}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={handleOnClose} variant="outline" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant={modeConfig.buttonVariant}
          loading={isSubmitting}
          disabled={
            (mode === WriteOffStatus.ESCALATED &&
              (formData.feesCut || 0) + (formData.expensesCut || 0) <= 0) ||
            (modeConfig.fields.some(f => f.type === 'amount') && !areAllAmountFieldsValid) ||
            (modeConfig.fields.some(f => f.type === 'comment' && f.required) &&
              !formData.comment.trim()) ||
            (modeConfig.fields.some(f => f.type === 'confirmation') &&
              !formData.isConfirmed) ||
            (modeConfig.fields.some(f => f.type === 'allocation-status') &&
              Number(((formData.feesCut || 0) + (formData.expensesCut || 0) + (formData.approvedFeesCut || 0) + (formData.approvedExpensesCut || 0)).toFixed(2)) !== (writeOff?.amount || 0))
          }
        >
          {modeConfig.buttonText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
